// =============================================================================
// feuilleGlobale.js — LA feuille de composants d'Aramis, à ORDRE EXPLICITE
// (refonte « Mosaïque vivante », lot L2 « Fondations », 27/09/2026).
//
// POURQUOI. Jusqu'ici le style arrivait par couches dont l'ordre n'était écrit
// nulle part : theme.css (importé) PUIS la feuille injectée d'App.jsx (ajoutée
// au montage, donc plus tard dans le <head>). À spécificité égale la plus
// tardive gagne : les raffinements « Phase 2 » de theme.css ont ainsi été
// rabotés sans bruit par les règles d'App.jsx pendant trois mois. Et le
// <style> d'AramisGlobalStyles, rendu dans le <body> par Header.js, passait
// APRÈS le socle mobile.
//
// L'ORDRE, désormais écrit ici et vérifié par src/feuilleGlobale.test.js :
//   zone STATIQUE (toutes les pages, importée par src/index.js)
//     1. design-system/ds-tokens.css  jetons du DS (vendorisés, intouchés)
//     2. theme.css                    jetons Aramis : RIEN QUE des jetons
//     3. primitivesGlobales.css       keyframes et utilitaires sans homonyme,
//                                     utiles hors d'App (fournisseurs racine),
//                                     et filets de focus :where() qui DOIVENT
//                                     perdre contre les composants
//   zone INJECTÉE (App.jsx, au montage) : UN SEUL <style id="enio-css5">
//     4. composants   ex-injectCSS d'App.jsx (reset, .card, .btn, .inp…)
//     5. raffinements ex-règles de theme.css (passent APRÈS les composants)
//     6. effets       ex-<AramisGlobalStyles/> (uxComponents.js)
//     7. coquille     typographie de coquille, plaque navy et en-tête de page
//                     (lot L4 : PlaqueModule.js, EnTetePage.js)
//     8. primitives   fenêtres, toasts, chiffres, jauges, dépliage, squelettes
//                     (lot L5, CSS_PRIMITIVES ci-dessous)
//     9. socle        SOCLE MOBILE d'App.jsx — TOUJOURS DERNIER
//
// Règle : une règle qui doit l'emporter sur un composant va dans
// « raffinements », jamais dans theme.css (qui ne porte que des jetons). Un
// lot qui a besoin d'une nouvelle section l'ajoute à ORDRE_SECTIONS, AVANT
// « socle », et met à jour src/feuilleGlobale.test.js.
// =============================================================================

// Feuille de la COQUILLE (lot L6) : en-tête, rail, onglets, feuilles, palette,
// transitions de vue. Assemblée en FIN de raffinements (plus bas).
import { CSS_CHASSIS } from "./coquilleStyles";

export const ID_FEUILLE = "enio-css5";
export const ORDRE_SECTIONS = Object.freeze(["composants", "raffinements", "effets", "coquille", "primitives", "socle"]);

/** Assemble les sections dans l'ordre imposé. Lève si une section manque ou si
 *  une section inconnue est fournie : un oubli ne doit pas passer en silence. */
export function composerFeuille(sections = {}) {
  const manquantes = ORDRE_SECTIONS.filter((k) => typeof sections[k] !== "string");
  if (manquantes.length) throw new Error("feuilleGlobale : section(s) manquante(s) : " + manquantes.join(", "));
  const inconnues = Object.keys(sections).filter((k) => !ORDRE_SECTIONS.includes(k));
  if (inconnues.length) throw new Error("feuilleGlobale : section(s) inconnue(s) : " + inconnues.join(", "));
  return ORDRE_SECTIONS
    .map((k, i) => `\n/* ═════ SECTION ${i + 1}/${ORDRE_SECTIONS.length} : ${k.toUpperCase()} ═════ */\n${sections[k]}`)
    .join("\n");
}

/** Complète la section RAFFINEMENTS après coup, pour une feuille portée par un
 *  morceau paresseux (lot L6 : palette ⌘K, feuilles « Modules » et « Moi ») :
 *  le texte est inséré juste avant la section des effets, donc l'ordre écrit
 *  tient et le socle mobile reste le dernier bloc. Une fois par clé. Renvoie
 *  false si la feuille globale n'est pas posée (tests, pages hors App). */
export function ajouterAuxRaffinements(cle, css) {
  if (typeof document === "undefined") return false;
  const s = document.getElementById(ID_FEUILLE);
  if (!s) return false;
  const repere = `/* ⟦raffinement différé : ${cle}⟧ */`;
  if (s.textContent.includes(repere)) return true;
  const i = ORDRE_SECTIONS.indexOf("effets");
  const entete = `/* ═════ SECTION ${i + 1}/${ORDRE_SECTIONS.length} : EFFETS ═════ */`;
  const j = s.textContent.indexOf(entete);
  if (j < 0) return false;
  s.textContent = s.textContent.slice(0, j) + repere + "\n" + css + "\n" + s.textContent.slice(j);
  return true;
}

/** Pose la feuille une seule fois (idempotent, comme l'ancien injectCSS). */
export function installerFeuilleGlobale(sections) {
  if (typeof document === "undefined") return null;
  const deja = document.getElementById(ID_FEUILLE);
  if (deja) return deja;
  const s = document.createElement("style");
  s.id = ID_FEUILLE;
  s.textContent = composerFeuille(sections);
  document.head.appendChild(s);
  return s;
}

// ── 5. RAFFINEMENTS — déplacés TELS QUELS de theme.css (lot L2) ──────────────
// Deux changements de forme, aucun de fond : les accents graves des
// commentaires sont devenus « » (un accent grave fermerait ce gabarit JS), et
// chaque commentaire CSS s'écrit ${/* … */""} — lisible ici, à sa place, mais
// retiré du bundle par la minification (le CSS produit est le même, sans ses
// commentaires). Placés APRÈS les composants, ces raffinements l'emportent
// désormais à spécificité égale : c'est tout l'objet du lot. Au repos, rien ne
// change (vérifié capture contre capture).
export const CSS_RAFFINEMENTS = `
${/* ============================================================================
   PHASE 2 — Effets Mosaïque (6/07/2026)
   Couche de raffinement SÉLECTIVE : boutons, en-têtes de module, toasts,
   modale de confirmation, cartes du cockpit. Les dégradés/ombres colorées
   descendent des tokens --acc-* ; tout est neutralisé par
   prefers-reduced-motion. Pas de filter/blur sur les grandes surfaces
   (performance iPad) — le verre est réservé aux petits éléments.
   ============================================================================ */""}

${/* ── Boutons pleins (lot L4) : des APLATS. Plus de dégradé à 135° ni d'ombre
   colorée (synthèse §4.4, décisions v2 : aucun dégradé décoratif). L'action
   est navy : .btn-orange (blanc sur orange, 2,25:1) devient l'action navy,
   carte claire la nuit ; .btn-green passe à l'encre navy profond. ─────── */""}
.btn.btn-orange{ background:var(--acc-navy); color:var(--plate-ink); box-shadow:none; }
body.theme-dark .btn.btn-orange{ background:var(--plate-cta); color:var(--plate-cta-ink); }
${/* Bandeau de commande (décision de Pierre du 27/10/2026) : .btn-principal
   est le NOM de l'action principale d'une page (le geste qui crée son
   contenu), peinte comme .btn-orange : aplat navy, carte claire la nuit. Une
   seule par page, au bord droit du bandeau (src/grammaireEnTetes.test.js). */""}
.btn.btn-principal{ background:var(--acc-navy); color:var(--plate-ink); box-shadow:none; }
body.theme-dark .btn.btn-principal{ background:var(--plate-cta); color:var(--plate-cta-ink); }
${/* Contour des boutons SECONDAIRES (.btn-ghost) : « un bouton a l'air d'un
   bouton ». Au repos, le jeton --btn-ghost-border (theme.css) atteint 3:1 sur
   la carte, le fond de l'app et les encarts, dans les deux thèmes (calculé
   par src/grammaireEnTetes.test.js) ; au survol, l'encre orange du thème
   (--tx-orange : 5:1 le jour, 6:1 la nuit), et non plus l'orange brut, qui
   tombait à 2,1:1 sur une carte claire. */""}
.btn.btn-ghost{ border-color:var(--btn-ghost-border); }
@media (hover: hover) and (pointer: fine){
  .btn.btn-ghost:hover:not(:disabled){ border-color:var(--tx-orange); }
}
.btn.btn-teal{   background:var(--acc-teal);   box-shadow:none; }
.btn.btn-violet{ background:var(--acc-violet); box-shadow:none; }
.btn.btn-red{    background:var(--acc-red);    box-shadow:none; }
.btn.btn-green{  background:var(--acc-green);  color:var(--acc-navy-deep); box-shadow:none; }
.btn.btn-navy{   background:var(--acc-navy-soft); box-shadow:none; }
.btn.btn-gold{   background:var(--acc-orange); box-shadow:none; }
${/* SEULE source de la transition, du survol, de l'appui et du focus de .btn :
   les composants (App.jsx) ne doivent pas les redéclarer. Depuis L2 cette
   section passe APRÈS eux et l'emporterait, mais deux sources pour un même
   état, c'est une de trop (audit mouvement u3, H1 ; verrou :
   src/cascadeBoutons.test.js). */""}
.btn{ transition:transform var(--duration-fast, 120ms) var(--ease-out, cubic-bezier(.16,1,.3,1)), box-shadow .22s ease, filter .2s ease, background .2s ease, border-color .2s ease, color .2s ease; }
${/* Sur iPhone il n'existe pas de « sortie de survol » : après un tap, l'état
   :hover reste COLLÉ jusqu'à ce qu'on touche ailleurs — un bouton restait
   éclairci, une carte du cockpit restait soulevée de 4 px au retour sur la
   page. On réserve donc tout :hover au couple souris/trackpad, et on garde un
   retour au doigt par :active. */""}
@media (hover: hover) and (pointer: fine){
  .btn:hover:not(:disabled){ filter:brightness(1.04); }
}
${/* Appui : le bouton s'enfonce (97 %) en 120 ms, au doigt comme à la souris. */""}
.btn:active:not(:disabled){ transform:scale(var(--motion-press, .97)); }
${/* Focus clavier : teal de la charte (et non plus le magenta de --accent). */""}
.btn:focus-visible{ outline:2px solid var(--focus); outline-offset:2px; }
${/* Les filets de focus :where(…):focus-visible (tout élément interactif qui
   n'en pose pas lui-même) ne sont PAS ici : ils vivent dans la zone statique,
   src/primitivesGlobales.css, AVANT les composants, pour perdre contre eux
   (.inp{outline:none}). Placés ici, APRÈS les composants, ils doublaient la
   bordure et l'anneau des 833 champs .inp dès le clic (critique L2, M1). */""}

${/* ── En-tête de module (ModuleHero) : plus de bannière ni de halo ──────────
   Lot L4 : ModuleHero rend l'en-tête compact (EnTetePage.js) ; le halo des
   bannières (figé en L0) disparaît avec la bannière en dégradé. */""}

${/* ── Cartes du cockpit : relief au survol depuis les tokens ───────────────── */""}
button.ck-cta, button.ck-stat{ transition:transform .2s ease, box-shadow .22s ease, background .3s ease; }
@media (hover: hover) and (pointer: fine){
  button.ck-cta:hover, button.ck-stat:hover{ transform:translateY(-4px); box-shadow:var(--shadow-elevated); }
}
${/* Au doigt, l'enfoncement remplace le soulèvement : retour immédiat, rien qui
   reste en l'air une fois le doigt levé. RÉSERVÉ au tactile : au clavier-souris
   ces cartes n'avaient aucun retour d'enfoncement avant l'audit, le survol
   (ci-dessus) suffit et reste le seul effet du bureau. */""}
@media (pointer: coarse){
  button.ck-cta:active, button.ck-stat:active{ transform:scale(.98); }
}

${/* Mouvement réduit : réduire, pas supprimer. Les fondus restent ; les
   déplacements, les agrandissements et les halos qui battent disparaissent.
   Les variantes des keyframes partagées (enioRise, pulseLive) vivent avec
   elles dans src/primitivesGlobales.css. */""}
@media (prefers-reduced-motion: reduce){
  .btn, button.ck-cta, button.ck-stat{ transition:none; }
}

${/* ── Chantier 4 — helpers responsive (CSS-only) ───────────────────────────────
   Regroupe les ajustements pilotés par media-query pour éviter des listeners
   resize dans des listes chaudes. */""}
@media (max-width:600px){
  ${/* Vue projets simplifiée : on masque le libellé texte de maturité (la pastille
     de couleur reste) pour rendre de la largeur au titre sur mobile. */""}
  .simplifie-mat-label{ display:none; }
  ${/* Onglets de pilotage : scroll horizontal plutôt que retour à la ligne. */""}
  .pilotage-tabs{ flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; }
}

${/* ── Taille de police « Grand » / « Très grand » : rattraper le palier perdu ──
   applyFontSize() (appShared.js) agrandit par un « zoom » sur <body> : la mise en
   page ne fait plus que 333 px (Grand, ×1,18) ou 285 px (Très grand, ×1,38) sur
   un iPhone de 393 px, MAIS les media queries continuent de mesurer 393 px. Les
   règles « petit mobile » d'App.jsx (max-width:375px) ne se déclenchent donc
   jamais, et les grilles restent à deux ou trois colonnes pour ceux qui ont
   justement besoin de gros caractères. On rejoue ici le palier des 375 px à sa
   largeur d'écran RÉELLE équivalente : 375 × 1,18 ≈ 443 px, 375 × 1,38 ≈ 518 px.
   La classe body.fs-* est posée par applyFontSize ; la spécificité (0,2,0) passe
   devant les règles d'App.jsx quel que soit l'ordre d'injection. */""}
@media (max-width:443px){
  body.fs-lg .grid4{ grid-template-columns:1fr; }
}
@media (max-width:518px){
  body.fs-xl .grid3, body.fs-xl .grid4{ grid-template-columns:1fr; }
}

${/* ── Largeur des pages : UNE règle (décisions de Pierre du 05/10/2026 et du
   27/10/2026) ──────────────────────────────────────────────────────────────
   La racine d'une page de la coquille occupe toute la colonne de la coquille,
   à tous les écrans : le jeton --content-max (theme.css) vaut 100 % depuis
   le 27/10/2026, sans palier (les quatre paliers d'App.jsx laissaient des
   marges vides). Gouttière de 16 px, resserrée en haut et en bas sous
   640 px comme les pages de référence (PLF & PLFSS, Radar). Une page qui garde sa propre classe de racine y écrit
   max-width:var(--content-max) et margin:0 auto, sans repli. Les fenêtres et
   les panneaux gardent leur largeur ; le texte courant prend toute la sienne
   (règle suivante). Verrou : src/largeurPages.test.js. */""}
.page-largeur{ box-sizing:border-box; width:100%; max-width:var(--content-max); margin:0 auto; padding:20px 16px 48px; min-width:0; }
@media (max-width:640px){
  .page-largeur{ padding:12px 16px 40px; }
}

${/* ── Texte courant : toute la largeur ; JUSTIFIÉ seulement quand il occupe la
   largeur de la page (décision de Pierre du 12/10/2026, contre-relecture du
   même jour). Aucun texte courant n'est borné (ni max-width, ni colonne en
   ch) : il prend toute la largeur de son conteneur, partout. La
   justification, elle, suit une LISTE D'INCLUSION : un texte courant posé
   directement dans une colonne de page (racine de la page, sous-page,
   colonne de lecture d'un rapport : .page-largeur, .ms-page, .de-page, .ap-page, .rd-page, .lf-page, .lfr, .mc-page, .ib-page, .adm-cadre, .adm-page, .tgx-bloc2, .gv-guichet, .ho-page, .evl, .evc-page, .jourj--page, .hwl, .md-page, .rh-page, .cf-page, .vac-racine, .bt-page, .dep-page, .ax-module, .ax-console, .ax-fiche, .es-page, .ndf, .rh-ent, .rh-cond, .rap-etape, [style*="max-width: var(--content-max)"]ONNES ci-dessous), dans une enveloppe
   nue de cette colonne (div, section ou article sans classe ni style) ou
   dans l'index pleine largeur d'une colonne (HubSection hors deux colonnes).
   Jamais dans une carte, une Note, une plaque, une cellule de grille, une
   rangée flexible, un encart, une fenêtre, un panneau : ils ne sont pas dans
   la liste, leur texte reste aligné au début et SANS césure (la césure ne
   sert qu'à la justification). Texte courant : tout <p> sans classe, et tout
   <p>, <div> ou <blockquote> dont une classe finit par un nom de texte
   (-aide, -texte, -note, -p, -d, -m, -chapo, -intro, -consigne…) et qui ne
   contient aucun bloc. Spécificité nulle : une classe qui aligne autrement
   l'emporte. La colonne de la coquille (.cq-contenu) porte trois jetons ; ils
   retombent (le texte hérite alors de son parent, comme avant) sous 640 px
   (téléphone, fenêtre de bureau étroite, iPad en écran partagé ; 756 et
   884 px en « Grand » et « Très grand »), sur html[data-coquille="tel"]
   (iPhone en paysage) et à l'impression ; hors de la coquille, rien ne les
   porte. La césure ne coupe que les mots longs (huit lettres, quatre de
   chaque côté) et jamais un code, une valeur technique (.valeur-technique),
   un lien, un champ ou un bouton. Verrou : src/textesPleineLargeur.test.js. */""}
.cq-contenu{ --align-texte:justify; --cesure-texte:auto; --habillage-texte:pretty; }
:where(.cq-contenu) :where(
    :where(.page-largeur, .ms-page, .de-page, .ap-page, .rd-page, .lf-page, .lfr, .mc-page, .ib-page, .adm-cadre, .adm-page, .tgx-bloc2, .gv-guichet, .ho-page, .evl, .evc-page, .jourj--page, .hwl, .md-page, .rh-page, .cf-page, .vac-racine, .bt-page, .dep-page, .ax-module, .ax-console, .ax-fiche, .es-page, .ndf, .rh-ent, .rh-cond, .rap-etape, [style*="max-width: var(--content-max)"]),
    :where(.page-largeur, .ms-page, .de-page, .ap-page, .rd-page, .lf-page, .lfr, .mc-page, .ib-page, .adm-cadre, .adm-page, .tgx-bloc2, .gv-guichet, .ho-page, .evl, .evc-page, .jourj--page, .hwl, .md-page, .rh-page, .cf-page, .vac-racine, .bt-page, .dep-page, .ax-module, .ax-console, .ax-fiche, .es-page, .ndf, .rh-ent, .rh-cond, .rap-etape, [style*="max-width: var(--content-max)"]) > :where(div:not([class]):not([style]), section:not([class]), article:not([class])),
    :where(.page-largeur, .ms-page, .de-page, .ap-page, .rd-page, .lf-page, .lfr, .mc-page, .ib-page, .adm-cadre, .adm-page, .tgx-bloc2, .gv-guichet, .ho-page, .evl, .evc-page, .jourj--page, .hwl, .md-page, .rh-page, .cf-page, .vac-racine, .bt-page, .dep-page, .ax-module, .ax-console, .ax-fiche, .es-page, .ndf, .rh-ent, .rh-cond, .rap-etape, [style*="max-width: var(--content-max)"]) > .hub-sec:not(.hub-sec--2col) > .hub-liste
  ) > :where(p:not([class]), :where(p, div, blockquote):where(.texte-courant,
    [class$="-aide"], [class*="-aide "], [class$="-aides"], [class*="-aides "], [class$="-texte"], [class*="-texte "], [class$="-textes"],
    [class*="-textes "], [class$="-note"], [class*="-note "], [class$="-notes"], [class*="-notes "], [class$="-p"], [class*="-p "], [class$="-d"],
    [class*="-d "], [class$="-m"], [class*="-m "], [class$="-paragraphe"], [class*="-paragraphe "], [class$="-para"], [class*="-para "],
    [class$="-intro"], [class*="-intro "], [class$="-chapo"], [class*="-chapo "], [class$="-prose"], [class*="-prose "], [class$="-accroche"],
    [class*="-accroche "], [class$="-resume"], [class*="-resume "], [class$="-consigne"], [class*="-consigne "], [class$="-explication"],
    [class*="-explication "], [class$="-desc"], [class*="-desc "], [class$="-description"], [class*="-description "], [class$="-message"],
    [class*="-message "], [class$="-msg"], [class*="-msg "], [class$="-citation"], [class*="-citation "], [class$="-extrait"], [class*="-extrait "],
    [class$="-formule"], [class*="-formule "], [class$="-enbref"], [class*="-enbref "], [class$="-these"], [class*="-these "], [class$="-methode"],
    [class*="-methode "], [class$="-phrase"], [class*="-phrase "], [class$="-mention"], [class*="-mention "], [class$="-conseil"],
    [class*="-conseil "]):not(:has(> :is(address, article, aside, blockquote, dd, details, dialog, div, dl, dt, fieldset, figure, footer, form, h1, h2, h3, h4, h5, h6, header, hr, li, main, nav, ol, p, pre, section, table, ul))))
{ text-align:var(--align-texte); -webkit-hyphens:var(--cesure-texte); hyphens:var(--cesure-texte); hyphenate-limit-chars:8 4 4; -webkit-hyphenate-limit-before:4; -webkit-hyphenate-limit-after:4; text-wrap:var(--habillage-texte); }
:where(code, kbd, samp, var, pre, a, input, textarea, select, button, .valeur-technique, [class$="-code"], [class*="-code "]){ -webkit-hyphens:manual; hyphens:manual; }
:where(code, kbd, samp, .valeur-technique, [class$="-code"], [class*="-code "]){ text-align:start; overflow-wrap:break-word; }
@media (max-width:640px){
  .cq-contenu{ --align-texte:initial; --cesure-texte:initial; --habillage-texte:initial; }
}
html[data-coquille="tel"] .cq-contenu{ --align-texte:initial; --cesure-texte:initial; --habillage-texte:initial; }
@media (max-width:756px){
  body.fs-lg .cq-contenu{ --align-texte:initial; --cesure-texte:initial; --habillage-texte:initial; }
}
@media (max-width:884px){
  body.fs-xl .cq-contenu{ --align-texte:initial; --cesure-texte:initial; --habillage-texte:initial; }
}
@media print{
  .cq-contenu{ --align-texte:initial; --cesure-texte:initial; --habillage-texte:initial; }
}

${/* ── Pilotage (Dépenses & Boîte à outils) — layout à rail latéral ──────────────
   Données à gauche (dominant), réglages/outils dans un rail sticky à droite ;
   sous 1080px le rail passe sous les données (une colonne). */""}
.pilotage-layout{ display:grid; grid-template-columns:minmax(0,1fr) 320px; gap:18px; align-items:start; }
.pilotage-main{ min-width:0; display:grid; gap:18px; }
${/* Plus de position:sticky : le rail accroché à l'écran pendant le défilement
   donnait l'impression de flotter PAR-DESSUS le contenu (retour Pierre 05/08,
   géométrie vérifiée saine par sonde — le ressenti venait du sticky). */""}
.pilotage-rail{ display:grid; gap:12px; align-self:start; min-width:0; }
${/* Une carte de rail plus large que sa colonne (table, chips…) ne doit JAMAIS
   déborder sur le contenu principal ni élargir la page : elle défile en interne. */""}
.pilotage-rail > *{ min-width:0; max-width:100%; box-sizing:border-box; overflow-x:auto; }
@media (max-width:1080px){
  .pilotage-layout{ grid-template-columns:1fr; }
}

${/* ── Coquille (lot L6) : src/coquilleStyles.js ─────────────────────────── */""}
${CSS_CHASSIS}
`;

// ── 7. COQUILLE — typographie de coquille et plaques (lot L4, 28/09/2026) ────
// Plaque navy des accueils de module (src/PlaqueModule.js) et en-tête compact
// des pages de travail (src/EnTetePage.js, rendu de <ModuleHero>). Placée
// APRÈS les effets et AVANT le socle : le socle mobile garde le dernier mot
// (cibles de 44 pt, boutons sécables au doigt). Aucun hex (cliquet) : jetons
// --plate-*, --fs-*, --radius-*, --elev-* de theme.css. Light 300 jamais sous
// 20 px ; repli en 400 pour les tailles de texte « Grand » et « Très grand »
// (body.fs-lg / body.fs-xl, appShared.applyFontSize), synthèse §4.2.
// Largeurs étroites HORS téléphone (Split View, Slide Over, fenêtre de bureau
// réduite ; relecture L9a0, 29/09) : la plaque garde sa ligne tant que le
// titre y tient (« Gouvernance », 279 px à 44 px), réserve de 132 px sous
// 800 px, puis passe en colonne sous 680 px, comme au téléphone ; sous 480 px
// le titre prend la taille du téléphone (theme.css). Modèle géométrique verrouillé
// par typographieCoquille.test.js, de 320 à 1 440 px.
export const CSS_COQUILLE = `
.plaque{position:relative;overflow:hidden;overflow:clip;isolation:isolate;display:flex;align-items:center;gap:24px;min-width:0;min-height:152px;margin:0 0 24px;padding:28px 240px 28px 36px;border-radius:var(--radius-plaque);background:var(--plate);color:var(--plate-ink);box-shadow:var(--elev-plate),inset 0 0 0 1px var(--plate-edge)}
.plaque-decor{position:absolute;z-index:-1;right:-18px;top:-28px;width:208px;height:208px;opacity:.9;pointer-events:none}
.plaque-tuile,.plaque-logo{align-self:flex-start}
.plaque-logo{width:64px;height:64px;flex:none;box-sizing:border-box;border-radius:var(--radius-tile);object-fit:contain;padding:8px;background:var(--plate-cta)}
.plaque-texte{flex:1 1 auto;min-width:0}
.plaque-titre{margin:0;font-size:var(--fs-display);font-weight:300;line-height:1.05;letter-spacing:calc(-.028em * var(--em-ajuste, 1));color:var(--plate-ink);text-wrap:balance;overflow-wrap:anywhere}
.plaque-ligne{display:flex;flex-wrap:wrap;align-items:center;gap:12px 24px;margin-top:10px}
.plaque-etat{flex:1 1 320px;min-width:0;font-size:var(--fs-accroche);line-height:1.45;color:var(--plate-muted);text-wrap:pretty}
.plaque-actions{flex:none;display:flex;flex-wrap:wrap;gap:10px}
.plaque-pastille{display:inline-flex;align-items:center;gap:8px;margin-top:14px;padding:5px 12px;border-radius:var(--radius-pill);box-shadow:inset 0 0 0 1px var(--plate-ligne);font-size:var(--fs-meta);font-weight:600;line-height:1.3;color:var(--plate-ink)}
.plaque-pastille i{width:8px;height:8px;flex:none;border-radius:var(--radius-tile);transform:rotate(45deg);background:var(--acc-green)}
.plaque-pastille[data-attente] i{background:var(--acc-orange)}
.btn.btn-plaque{background:var(--plate-cta);color:var(--plate-cta-ink)}
.btn.btn-plaque2{background:transparent;color:var(--plate-ink);box-shadow:inset 0 0 0 1px var(--plate-ligne)}
html[data-coquille="tel"] .plaque{flex-direction:column;align-items:flex-start;gap:16px;min-height:0;margin-bottom:16px;padding:22px 20px 24px}
html[data-coquille="tel"] .plaque-decor{right:-14px;top:-28px;width:120px;height:120px}
html[data-coquille="tel"] .plaque-titre{margin-right:52px}
html[data-coquille="tel"] .plaque-etat{font-size:var(--fs-corps)}
html[data-coquille="tel"] .plaque-actions{flex:1 1 100%}
html[data-coquille="tel"] .plaque-actions .btn{flex:1 1 auto}
@media (max-width:800px){
  .plaque{padding-right:132px}
  .plaque-decor{right:-14px;top:-28px;width:120px;height:120px}
}
@media (max-width:680px){
  html:not([data-coquille="tel"]) .plaque{flex-direction:column;align-items:flex-start;gap:16px;min-height:0;padding:22px 20px 24px}
  html:not([data-coquille="tel"]) .plaque-actions{flex:1 1 100%}
}
.entete-page{margin:0 0 12px;min-width:0}
.entete-fil{display:flex;flex-wrap:wrap;align-items:center;column-gap:6px;margin:0 0 2px;font-size:var(--fs-meta);font-weight:500;line-height:16px;color:var(--text-sub)}
.entete-sep{color:var(--tx-teal)}
.entete-cur{font-weight:600;color:var(--text-main)}
${/* La ligne passe à la ligne et le texte garde au moins 260 px (moins la
   tuile, au téléphone) : en « Très grand » au bureau, quatre actions rigides
   (flex:none) écrasaient le titre sur une lettre de large (« Missio / ns »,
   « E / n / t / r… » des Entretiens RH, balayage du 12/10/2026). Les actions
   passent sous le titre et s'y replient. */""}
.entete-ligne{display:flex;flex-wrap:wrap;align-items:flex-start;gap:14px;min-width:0}
.entete-texte{flex:1 1 0;min-width:min(260px, calc(100% - 56px))}
.entete-titre{margin:0;font-size:var(--fs-page);font-weight:300;line-height:1.05;letter-spacing:calc(-.02em * var(--em-ajuste, 1));color:var(--text-main);text-wrap:balance;overflow-wrap:anywhere}
.entete-etat{margin-top:3px;font-size:var(--fs-meta);line-height:1.4;color:var(--text-sub);text-wrap:pretty}
.entete-actions{flex:0 1 auto;max-width:100%;min-width:0;display:flex;flex-wrap:wrap;align-items:center;gap:8px;align-self:center}
.entete-pastille{display:inline-flex;align-items:center;margin-left:8px;padding:2px 10px;border-radius:var(--radius-pill);background:var(--bg-card-inner);box-shadow:inset 0 0 0 1px var(--border-color);font-size:var(--fs-micro);font-weight:600;line-height:1.5;color:var(--text-sub);white-space:nowrap}
.entete-actions .entete-pastille{margin-left:0}
.entete-pastille-alerte{background:var(--toast-bg-warn);box-shadow:none;color:var(--tx-orange)}
html[data-coquille="tel"] .entete-fil{display:none}
html[data-coquille="tel"] .entete-ligne{flex-wrap:wrap}
html[data-coquille="tel"] .entete-actions{flex:1 1 100%}
body.fs-lg :is(.plaque-titre,.entete-titre),body.fs-xl :is(.plaque-titre,.entete-titre){font-weight:400}
`;

// ── 6. EFFETS — ex-<AramisGlobalStyles/> (uxComponents.js), déplacés tels quels
// Ils étaient rendus dans le <body> par Header.js, donc APRÈS le socle mobile,
// et n'existaient qu'une fois l'en-tête monté. Deux ajouts, sans effet au
// repos : le survol de .aramis-lift est gardé par le pointeur (classe posée
// nulle part au 27/09) ; sous mouvement réduit, les boucles s'arrêtent et
// l'apparition devient un simple fondu (synthèse §5.4 : réduire, pas supprimer).
// Commentaires éventuels écrits ${/* … */""}, comme ci-dessus.
export const CSS_EFFETS = `
@keyframes aramisPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(244, 152, 0,.4); }
  50%      { box-shadow: 0 0 0 8px rgba(244, 152, 0,0); }
}
@keyframes aramisFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes aramisShimmer {
  0%   { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}
.aramis-lift { transition: transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .18s ease; }
@media (hover: hover) and (pointer: fine){
  .aramis-lift:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0, 63, 89,.12); }
}
.aramis-fade-in { animation: aramisFadeIn .35s ease both; }
.aramis-shimmer {
  background: linear-gradient(90deg,
    var(--card-inner,#f3f4f6) 0%,
    rgba(255,255,255,.6) 50%,
    var(--card-inner,#f3f4f6) 100%);
  background-size: 200px 100%;
  animation: aramisShimmer 1.4s infinite linear;
}
@media (prefers-reduced-motion: reduce){
  @keyframes aramisFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .aramis-shimmer { animation: none; }
}
`;

// ── 8. PRIMITIVES — fenêtres, toasts, chiffres, jauges, dépliage, squelettes
// (lot L5 « Fenêtres, toasts, chiffres », 28/09/2026 ; synthèse §5.2 et §10).
// Classes NEUVES, sans homonyme ailleurs : placées APRÈS les effets (elles
// affinent .aramis-shimmer au thème sombre) et AVANT le socle mobile, qui garde
// le dernier mot au doigt. Les durées et courbes viennent des jetons de
// mouvement de theme.css ; sous mouvement réduit (système ou réglage
// data-mouvement), les déplacements partent et les fondus restent.
// Commentaires écrits ${/* … */""}, comme ci-dessus.
export const CSS_PRIMITIVES = `
${/* ── Fenêtres (<ModalShell>) : la feuille basse du téléphone ───────────────
   Poignée de 22 px (zone du geste, avec l'en-tête), prolongement de 120 px
   sous la feuille pour que l'élastique vers le haut ne découvre jamais le
   voile, curseur de saisie au pointeur fin. L'entrée et la sortie sont
   jouées en WAAPI par uxComponents.js. */""}
.fen-carte[data-mode="feuille"]::after{ content:""; position:absolute; left:0; right:0; top:100%; height:120px; background:inherit; pointer-events:none; }
.fen-poignee{ flex:none; height:22px; display:grid; place-items:center; touch-action:none; cursor:grab; }
.fen-poignee::before{ content:""; width:36px; height:5px; border-radius:var(--radius-pill, 999px); background:color-mix(in srgb, var(--text-sub) 38%, transparent); }
${/* L'en-tête ne rétrécit jamais sous son titre : la carte est une colonne
   flexible bornée en hauteur, et un en-tête en `flex: 0 1 auto` (min-height
   posé) retombait à 44 px quand le corps était long ; un titre de deux lignes
   ou plus en débordait alors, sous la poignée et sur le corps (relecture du
   05/10/2026, mesures hors loi : intitulé d'un avenant, Très grand). C'est le
   corps qui défile. */""}
.fen-carte[data-mode="feuille"] .fen-tete{ touch-action:none; min-height:var(--cible-min, 0px); flex-shrink:0; }
.fen-carte[data-glisse], .fen-carte[data-glisse] .fen-poignee{ cursor:grabbing; }
.fen-carte[data-glisse] .fen-corps{ pointer-events:none; }
${/* La bulle de messagerie (FloatingChat) passe au-dessus des fenêtres :
   sur une feuille, elle cacherait les boutons du bas. */""}
html[data-feuille-ouverte] [data-bulle-messagerie]{ visibility:hidden; }

${/* ── Toasts (src/toasts.js) : la pile ───────────────────────────────────────
   Chaque toast est posé en bas de la pile et monte de son décalage (--offset,
   calculé depuis les hauteurs mesurées). Transitions CSS, jamais de
   @keyframes : une entrée, une sortie ou un réagencement interrompu repart de
   sa position courante. Au téléphone : pleine largeur, au-dessus de la barre
   d'onglets. Elle monte au-dessus du bandeau de reconnexion tant qu'il est
   là (--h-bandeau-auth, posé par src/authGuard.js). */""}
.enio-toasts{ position:fixed; z-index:19000; right:calc(20px + var(--sar, 0px)); bottom:calc(20px + var(--h-onglets, 0px) + var(--h-bandeau-auth, 0px)); width:min(380px, calc(var(--vw100) - 40px)); height:0; pointer-events:none; }
html[data-coquille="tel"] .enio-toasts{ left:calc(12px + var(--sal, 0px)); right:calc(12px + var(--sar, 0px)); width:auto; bottom:calc(var(--h-onglets, 0px) + 10px + var(--h-bandeau-auth, 0px)); }
.enio-toast{
  position:absolute; right:0; bottom:0; width:100%; box-sizing:border-box;
  display:flex; align-items:flex-start; gap:10px; padding:11px 10px 11px 14px;
  background:var(--toast-bg-neutre); color:var(--toast-ink); border:1px solid var(--border-color);
  border-radius:var(--radius-md, 12px); box-shadow:var(--elev-1);
  font-size:var(--fs-meta); line-height:1.45; pointer-events:auto; touch-action:pan-y; user-select:none; -webkit-user-select:none;
  transform:translateY(calc(-1 * var(--offset, 0px))) translateX(var(--glisser, 0px));
  transition:transform var(--dur-modal) var(--ease-out), opacity var(--dur-view) var(--ease-out);
}
html[data-coquille="tel"] .enio-toast{ font-size:var(--fs-corps); }
.enio-toast[data-type="success"]{ background:var(--toast-bg-success); }
.enio-toast[data-type="error"]{ background:var(--toast-bg-error); }
.enio-toast[data-type="info"]{ background:var(--toast-bg-info); }
.enio-toast[data-type="warn"]{ background:var(--toast-bg-warn); }
.enio-toast[data-monte="false"]{ opacity:0; transform:translateY(calc(100% + 16px)); }
.enio-toast[data-cache="true"]{ opacity:0; pointer-events:none; }
.enio-toast[data-sortie="true"]{ opacity:0; pointer-events:none; transform:translateY(calc(-1 * var(--offset, 0px))) translateX(var(--sortie-x, 0px)) scale(var(--motion-scale-in, .96)); transition-duration:var(--dur-view); }
.enio-toast[data-glisse]{ transition:none; cursor:grabbing; }
.enio-toast-ico{ flex:none; display:inline-flex; margin-top:1px; color:var(--tx-blue); }
.enio-toast[data-type="success"] .enio-toast-ico{ color:var(--tx-green); }
.enio-toast[data-type="error"] .enio-toast-ico{ color:var(--tx-red); }
.enio-toast[data-type="warn"] .enio-toast-ico{ color:var(--tx-orange); }
.enio-toast-msg{ flex:1; min-width:0; overflow-wrap:anywhere; white-space:pre-line; }
.enio-toast-x{ flex:none; display:inline-grid; place-items:center; margin:-4px -2px -4px 0; min-width:28px; min-height:28px; padding:0; border:0; border-radius:var(--radius-sm, 8px); background:transparent; color:var(--toast-close); cursor:pointer; }
@media (hover:hover) and (pointer:fine){ .enio-toast-x:hover{ background:color-mix(in srgb, var(--toast-close) 12%, transparent); } }
@media (pointer:coarse){ .enio-toast-x{ min-width:var(--cible-min); min-height:var(--cible-min); margin:-10px -6px -10px 0; } }

${/* ── Odomètre (src/Odometre.js) : fenêtre de 1 em sur une bande de 30
   chiffres, dessinée par ::before (aucun nœud de texte dans le calque). Au
   repos, seul le texte réel est affiché ; pendant le roulement, il passe en
   texte pour lecteur d'écran et le calque prend sa place. */""}
.odo{ display:inline-block; position:relative; white-space:nowrap; }
.odo-v{ display:none; }
.odo[data-roule] .odo-v{ display:inline-flex; align-items:baseline; position:relative; vertical-align:baseline; }
.odo[data-roule] .odo-t{ position:absolute; width:1px; height:1px; margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; }
.odo-c{ position:relative; display:inline-block; height:calc(1em * var(--em-ajuste, 1)); line-height:1; clip-path:inset(0 calc(-0.7em * var(--em-ajuste, 1))); }
.odo-b{ display:block; will-change:transform; }
.odo-b::before{ content:attr(data-bande); display:block; white-space:pre; line-height:1; text-align:left; }
.odo-g{ display:inline-block; line-height:1; white-space:pre; }
.odo-g::before{ content:attr(data-g); }
.odo-v [data-fantome]{ position:absolute; top:0; }
.odo-sonde{ position:absolute; left:0; top:0; visibility:hidden; white-space:pre; line-height:1; pointer-events:none; }
${/* iPad sous la taille du texte (data-texte-ajuste="jetons", theme.css) : le
   calque n'a aucun nœud de texte, le zoom y atteint donc la police une
   seconde fois (la bande mesurait 41 et 57 px par ligne pour une fenêtre de
   35,4 et 41,4). L'odomètre repart de la taille DÉCLARÉE divisée par
   l'échelle, que le zoom ramène à la bonne ; son em n'est plus compté qu'une
   fois ; le texte réel, ramené par WebKit à sa taille déclarée, la reprend
   multipliée. */""}
:where(html[data-texte-ajuste="jetons"]) .odo{ font-size:calc(100% / var(--fs-zoom)); --em-ajuste:1; }
:where(html[data-texte-ajuste="jetons"]) .odo-t{ font-size:calc(100% * var(--fs-zoom)); }

${/* ── Jauges (src/Jauge.js) : le remplissage s'étire en scaleX depuis le bord
   de début ; la piste arrondit et rogne. 480 ms --ease-out-soft. */""}
.jauge{ position:relative; overflow:hidden; }
.jauge-plein{ position:absolute; inset:0; transform-origin:0 50%; transition:transform 480ms var(--ease-out-soft); }
.jauge[data-rapide] > .jauge-plein{ transition-duration:var(--dur-roll); }

${/* ── Dépliage (src/Deplier.js) : grid-template-rows 0fr → 1fr, 220 ms ; le
   contenu n'est rogné que pendant le mouvement. Chevron : 90° en 180 ms. */""}
.deplier{ display:grid; grid-template-rows:1fr; opacity:1; transition:grid-template-rows 220ms var(--ease-out), opacity var(--dur-fast) var(--ease-out); }
.deplier[data-etat="ferme"]{ grid-template-rows:0fr; opacity:0; }
.deplier-int{ min-height:0; min-width:0; }
.deplier:not([data-etat="ouvert"]) > .deplier-int{ overflow:hidden; }
.deplier-chevron{ display:inline-flex; transition:transform 180ms var(--ease-out); }
.deplier-chevron[data-ouvert="true"]{ transform:rotate(90deg); }

${/* ── Squelettes (src/squelettes.js) : blocs du reflet partagé ; au thème
   sombre, un reflet discret (le blanc à 60 % d'origine y faisait une barre
   lumineuse). */""}
.squelette-bloc{ display:inline-block; vertical-align:middle; flex:none; }
.squelette-sr{ position:absolute; width:1px; height:1px; margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; }
body.theme-dark .aramis-shimmer{ background:linear-gradient(90deg, var(--card-inner) 0%, rgba(255,255,255,.07) 50%, var(--card-inner) 100%); background-size:200px 100%; }

${/* Mouvement réduit : réduire, pas supprimer. Fondus gardés, déplacements
   coupés (la position finale est posée d'un coup). */""}
@media (prefers-reduced-motion: reduce){
  .enio-toast{ transition:opacity var(--dur-fast) var(--ease-out); }
  .jauge-plein, .deplier-chevron{ transition:none; }
  .deplier{ transition:opacity var(--dur-fast) var(--ease-out); }
}
:root[data-mouvement="reduit"] .enio-toast{ transition:opacity var(--dur-fast) var(--ease-out); }
:root[data-mouvement="reduit"] .jauge-plein, :root[data-mouvement="reduit"] .deplier-chevron{ transition:none; }
:root[data-mouvement="reduit"] .deplier{ transition:opacity var(--dur-fast) var(--ease-out); }
`;
